import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TrendingUpIcon, TrendingDownIcon, PieChartIcon, CalendarIcon, CreditCardIcon, ShoppingBagIcon, UtensilsIcon, CarIcon, HomeIcon, SettingsIcon, BellIcon, TrendingUp, DollarSignIcon, AlertCircleIcon, CheckCircle2Icon, XCircleIcon, ArrowRightIcon, SparklesIcon, EyeIcon, ShieldCheckIcon, ClockIcon, ZapIcon } from 'lucide-react';
import { Button } from '../ui/Button';
import { BillPaymentCard } from '../ui/BillPaymentCard';
import { OPENFINANCE_THEME } from '../../constants/openFinanceTheme';
import { MOCK_UPCOMING_BILLS } from '../../constants/mockData';
export interface PFMAfterScreenProps {
  connectedBanks: Array<{
    name: string;
    logoUrl?: string;
  }>;
  onAddMoreAccounts: () => void;
  onManageConsents?: () => void;
  onPayBill?: (billId: string) => void;
  onSetupAutoPay?: (billId: string) => void;
}
export function PFMAfterScreen({
  connectedBanks,
  onAddMoreAccounts,
  onManageConsents,
  onPayBill,
  onSetupAutoPay
}: PFMAfterScreenProps) {
  const [showBeforeAfter, setShowBeforeAfter] = useState(false);
  const categories = [{
    name: 'Shopping',
    amount: 1245,
    icon: ShoppingBagIcon,
    color: 'bg-purple-500'
  }, {
    name: 'Food',
    amount: 890,
    icon: UtensilsIcon,
    color: 'bg-orange-500'
  }, {
    name: 'Transport',
    amount: 450,
    icon: CarIcon,
    color: 'bg-blue-500'
  }, {
    name: 'Bills',
    amount: 2100,
    icon: HomeIcon,
    color: 'bg-green-500'
  }];
  const recentTransactions = [{
    merchant: 'Carrefour',
    category: 'Shopping',
    amount: -125.5,
    date: 'Today',
    icon: ShoppingBagIcon
  }, {
    merchant: 'Uber',
    category: 'Transport',
    amount: -35.0,
    date: 'Today',
    icon: CarIcon
  }, {
    merchant: 'Salary Deposit',
    category: 'Income',
    amount: 8500.0,
    date: 'Yesterday',
    icon: TrendingUpIcon
  }, {
    merchant: 'Netflix',
    category: 'Entertainment',
    amount: -55.0,
    date: '2 days ago',
    icon: CreditCardIcon
  }];
  return <div className="space-y-6" data-id="element-1024">
      {/* Success Banner */}
      <motion.div initial={{
      opacity: 0,
      y: -20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5
    }} className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 rounded-2xl p-4" style={{
      borderColor: OPENFINANCE_THEME.colors.success + '40'
    }} data-id="element-1025">
        <div className="flex items-start gap-3" data-id="element-1026">
          <motion.div initial={{
          scale: 0
        }} animate={{
          scale: 1
        }} transition={{
          delay: 0.2,
          type: 'spring',
          stiffness: 200
        }} className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{
          backgroundColor: OPENFINANCE_THEME.colors.success
        }} data-id="element-1027">
            <CheckCircle2Icon className="w-6 h-6 text-white" data-id="element-1028" />
          </motion.div>
          <div className="flex-1" data-id="element-1029">
            <h3 className="text-base font-bold mb-1" style={{
            color: OPENFINANCE_THEME.colors.text,
            fontFamily: OPENFINANCE_THEME.fonts.heading
          }} data-id="element-1030">
              Welcome to VeloX! 🎉
            </h3>
            <p className="text-sm mb-3" style={{
            color: OPENFINANCE_THEME.colors.textMuted,
            fontFamily: OPENFINANCE_THEME.fonts.body
          }} data-id="element-1031">
              Your {connectedBanks.length} bank account
              {connectedBanks.length > 1 ? 's are' : ' is'} now connected. See
              all your finances in one place.
            </p>

            <div className="flex gap-2" data-id="element-1032">
              <button onClick={() => setShowBeforeAfter(!showBeforeAfter)} className="text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors" style={{
              backgroundColor: OPENFINANCE_THEME.colors.primary500,
              color: 'white'
            }} data-id="element-1033">
                {showBeforeAfter ? 'Hide' : 'See'} Before & After
              </button>
              {onManageConsents && <button onClick={onManageConsents} className="text-xs font-semibold px-3 py-1.5 rounded-lg border-2 transition-colors hover:bg-gray-50" style={{
              borderColor: OPENFINANCE_THEME.colors.border,
              color: OPENFINANCE_THEME.colors.text
            }} data-id="element-1034">
                  Manage Consents
                </button>}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Before/After Comparison */}
      <AnimatePresence data-id="element-1035">
        {showBeforeAfter && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.3
      }} className="overflow-hidden" data-id="element-1036">
            <div className="grid md:grid-cols-2 gap-4" data-id="element-1037">
              {/* Before */}
              <motion.div initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.1
          }} className="bg-white border-2 rounded-2xl p-5" style={{
            borderColor: '#EF444430'
          }} data-id="element-1038">
                <div className="flex items-center gap-2 mb-4" data-id="element-1039">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{
                backgroundColor: '#EF444415'
              }} data-id="element-1040">
                    <XCircleIcon className="w-5 h-5 text-red-500" data-id="element-1041" />
                  </div>
                  <h3 className="text-lg font-bold" style={{
                color: OPENFINANCE_THEME.colors.text,
                fontFamily: OPENFINANCE_THEME.fonts.heading
              }} data-id="element-1042">
                    Before VeloX
                  </h3>
                </div>

                <ul className="space-y-2" data-id="element-1043">
                  {['Log into 3 different bank apps', 'Manually track spending in spreadsheets', 'No clear picture of total finances', 'Miss bill due dates', 'Can compare accounts easily'].map((item, index) => <li key={index} className="flex items-start gap-2 text-sm" style={{
                color: OPENFINANCE_THEME.colors.textMuted
              }} data-id="element-1044">
                      <XCircleIcon className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" data-id="element-1045" />
                      <span data-id="element-1046">{item}</span>
                    </li>)}
                </ul>
              </motion.div>

              {/* After */}
              <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.2
          }} className="bg-white border-2 rounded-2xl p-5" style={{
            borderColor: OPENFINANCE_THEME.colors.success + '30'
          }} data-id="element-1047">
                <div className="flex items-center gap-2 mb-4" data-id="element-1048">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{
                backgroundColor: OPENFINANCE_THEME.colors.success + '15'
              }} data-id="element-1049">
                    <CheckCircle2Icon className="w-5 h-5" style={{
                  color: OPENFINANCE_THEME.colors.success
                }} data-id="element-1050" />
                  </div>
                  <h3 className="text-lg font-bold" style={{
                color: OPENFINANCE_THEME.colors.text,
                fontFamily: OPENFINANCE_THEME.fonts.heading
              }} data-id="element-1051">
                    With VeloX
                  </h3>
                </div>
                <ul className="space-y-2" data-id="element-1052">
                  {['See all accounts in one dashboard', 'Automatic spending categorization', 'Total balance across all banks', 'Smart bill reminders', 'Compare rates and find savings'].map((item, index) => <li key={index} className="flex items-start gap-2 text-sm" style={{
                color: OPENFINANCE_THEME.colors.textMuted
              }} data-id="element-1053">
                      <CheckCircle2Icon className="w-4 h-4 flex-shrink-0 mt-0.5" style={{
                  color: OPENFINANCE_THEME.colors.success
                }} data-id="element-1054" />
                      <span data-id="element-1055">{item}</span>
                    </li>)}
                </ul>
              </motion.div>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Bill Reminders Section - NEW */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.15
    }} data-id="element-1056">
        <div className="flex items-center justify-between mb-4" data-id="element-1057">
          <div className="flex items-center gap-3" data-id="element-1058">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{
            background: `linear-gradient(135deg, ${OPENFINANCE_THEME.colors.accent500} 0%, ${OPENFINANCE_THEME.colors.accent600} 100%)`
          }} data-id="element-1059">
              <ZapIcon className="w-5 h-5 text-white" data-id="element-1060" />
            </div>
            <div data-id="element-1061">
              <h2 className="text-lg font-bold" style={{
              color: OPENFINANCE_THEME.colors.text,
              fontFamily: OPENFINANCE_THEME.fonts.heading
            }} data-id="element-1062">
                Upcoming Bills
              </h2>
              <p className="text-xs" style={{
              color: OPENFINANCE_THEME.colors.textMuted
            }} data-id="element-1063">
                Never miss a payment
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-3" data-id="element-1064">
          {MOCK_UPCOMING_BILLS.map(bill => <BillPaymentCard key={bill.id} merchantName={bill.merchantName} merchantLogo={bill.merchantLogo} amount={bill.amount} currency={bill.currency} dueDate={bill.dueDate} daysUntilDue={bill.daysUntilDue} hasVRP={bill.hasVRP} onPayNow={onPayBill ? () => onPayBill(bill.id) : undefined} onSetupAutoPay={onSetupAutoPay ? () => onSetupAutoPay(bill.id) : undefined} data-id="element-1065" />)}
        </div>
      </motion.div>

      {/* Financial Overview Cards */}
      <div className="grid md:grid-cols-3 gap-4" data-id="element-1066">
        {/* Total Balance */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2
      }} className="bg-gradient-to-br rounded-2xl p-5 text-white shadow-lg md:col-span-2" style={{
        background: `linear-gradient(135deg, ${OPENFINANCE_THEME.colors.primary500} 0%, ${OPENFINANCE_THEME.colors.primary600} 100%)`
      }} data-id="element-1067">
          <div className="flex items-start justify-between mb-4" data-id="element-1068">
            <div data-id="element-1069">
              <div className="text-xs font-medium opacity-80 uppercase tracking-wide mb-2" data-id="element-1070">
                Total Balance
              </div>
              <div className="text-4xl font-bold mb-1" data-id="element-1071">AED 32,450</div>
              <div className="flex items-center gap-2 text-sm" data-id="element-1072">
                <TrendingUpIcon className="w-4 h-4 text-green-300" data-id="element-1073" />
                <span className="text-green-300 font-semibold" data-id="element-1074">+12.5%</span>
                <span className="opacity-70" data-id="element-1075">vs last month</span>
              </div>
            </div>
            <div className="w-12 h-12 rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center" data-id="element-1076">
              <PieChartIcon className="w-6 h-6" data-id="element-1077" />
            </div>
          </div>

          {/* Connected Accounts Pills */}
          <div className="flex items-center gap-2 flex-wrap" data-id="element-1078">
            {connectedBanks.map((bank, i) => <div key={i} className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-3 py-1.5" data-id="element-1079">
                {bank.logoUrl && <img src={bank.logoUrl} alt={bank.name} className="w-4 h-4 rounded object-contain bg-white" data-id="element-1080" />}
                <span className="text-xs font-medium" data-id="element-1081">{bank.name}</span>
              </div>)}
            <button onClick={onAddMoreAccounts} className="inline-flex items-center gap-1 bg-white/10 backdrop-blur-sm rounded-full px-3 py-1.5 hover:bg-white/20 transition-colors" data-id="element-1082">
              <span className="text-xs font-medium" data-id="element-1083">+ Add Bank</span>
            </button>
          </div>
        </motion.div>

        {/* Spending This Month */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3
      }} className="bg-white rounded-2xl p-5 border-2 shadow-sm" style={{
        borderColor: OPENFINANCE_THEME.colors.border
      }} data-id="element-1084">
          <div className="flex items-center gap-2 mb-3" data-id="element-1085">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{
            backgroundColor: OPENFINANCE_THEME.colors.accent500 + '15'
          }} data-id="element-1086">
              <TrendingDownIcon className="w-5 h-5" style={{
              color: OPENFINANCE_THEME.colors.accent500
            }} data-id="element-1087" />
            </div>
          </div>
          <div className="text-xs font-medium uppercase tracking-wide mb-1" style={{
          color: OPENFINANCE_THEME.colors.textMuted
        }} data-id="element-1088">
            Spending This Month
          </div>
          <div className="text-2xl font-bold mb-1" style={{
          color: OPENFINANCE_THEME.colors.text
        }} data-id="element-1089">
            AED 4,685
          </div>
          <div className="flex items-center gap-1 text-sm" data-id="element-1090">
            <TrendingDownIcon className="w-4 h-4 text-green-500" data-id="element-1091" />
            <span className="text-green-500 font-semibold" data-id="element-1092">8% less</span>
            <span className="text-xs" style={{
            color: OPENFINANCE_THEME.colors.textMuted
          }} data-id="element-1093">
              than last month
            </span>
          </div>
        </motion.div>
      </div>

      {/* What VeloX Can Do Section */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.4
    }} className="bg-gradient-to-br from-indigo-50 to-purple-50 border-2 rounded-2xl p-6" style={{
      borderColor: OPENFINANCE_THEME.colors.primary500 + '20'
    }} data-id="element-1094">
        <div className="flex items-center gap-3 mb-5" data-id="element-1095">
          <motion.div animate={{
          rotate: [0, 360]
        }} transition={{
          repeat: Infinity,
          duration: 3,
          ease: 'linear'
        }} className="w-10 h-10 rounded-xl flex items-center justify-center" style={{
          background: `linear-gradient(135deg, ${OPENFINANCE_THEME.colors.primary500} 0%, ${OPENFINANCE_THEME.colors.accent500} 100%)`
        }} data-id="element-1096">
            <SparklesIcon className="w-5 h-5 text-white" data-id="element-1097" />
          </motion.div>
          <div data-id="element-1098">
            <h3 className="text-lg font-bold" style={{
            color: OPENFINANCE_THEME.colors.text,
            fontFamily: OPENFINANCE_THEME.fonts.heading
          }} data-id="element-1099">
              What VeloX Can Do for You
            </h3>
            <p className="text-sm" style={{
            color: OPENFINANCE_THEME.colors.textMuted
          }} data-id="element-1100">
              Powered by Open Finance
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4" data-id="element-1101">
          {[{
          icon: TrendingUp,
          title: 'Smart Insights',
          description: 'Automatic spending analysis shows you',
          color: OPENFINANCE_THEME.colors.success
        }, {
          icon: BellIcon,
          title: 'Bill Reminders',
          description: 'Never miss a payment - DEWA bill due in 3 days (AED 450)',
          color: OPENFINANCE_THEME.colors.warning
        }, {
          icon: PieChartIcon,
          title: 'Budget Tracking',
          description: "You've spent 65% of your dining budget (AED 890 of AED 1,200)",
          color: OPENFINANCE_THEME.colors.accent500
        }, {
          icon: DollarSignIcon,
          title: 'Find Savings',
          description: 'ADIB offers 3.5% on savings - 0.5% better than your current rate',
          color: OPENFINANCE_THEME.colors.primary500
        }].map((feature, index) => {
          const Icon = feature.icon;
          return <motion.div key={index} initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.5 + index * 0.1
          }} className="bg-white rounded-xl p-4 border-2" style={{
            borderColor: OPENFINANCE_THEME.colors.borderLight
          }} data-id="element-1102">
                <div className="flex items-start gap-3" data-id="element-1103">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{
                backgroundColor: feature.color + '15'
              }} data-id="element-1104">
                    <Icon className="w-5 h-5" style={{
                  color: feature.color
                }} data-id="element-1105" />
                  </div>
                  <div data-id="element-1106">
                    <h4 className="text-sm font-bold mb-1" style={{
                  color: OPENFINANCE_THEME.colors.text,
                  fontFamily: OPENFINANCE_THEME.fonts.heading
                }} data-id="element-1107">
                      {feature.title}
                    </h4>
                    <p className="text-xs" style={{
                  color: OPENFINANCE_THEME.colors.textMuted,
                  fontFamily: OPENFINANCE_THEME.fonts.body
                }} data-id="element-1108">
                      {feature.description}
                    </p>
                  </div>
                </div>
              </motion.div>;
        })}
        </div>
      </motion.div>

      {/* Spending Breakdown */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.6
    }} className="bg-white rounded-2xl p-5 border-2 shadow-sm" style={{
      borderColor: OPENFINANCE_THEME.colors.border
    }} data-id="element-1109">
        <div className="flex items-center justify-between mb-4" data-id="element-1110">
          <div data-id="element-1111">
            <div className="text-sm font-bold uppercase tracking-wide" style={{
            color: OPENFINANCE_THEME.colors.text
          }} data-id="element-1112">
              Spending by Category
            </div>
            <div className="text-xs" style={{
            color: OPENFINANCE_THEME.colors.textMuted
          }} data-id="element-1113">
              This month
            </div>
          </div>
        </div>

        <div className="space-y-3" data-id="element-1114">
          {categories.map((category, index) => {
          const percentage = category.amount / 4685 * 100;
          const Icon = category.icon;
          return <motion.div key={category.name} initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.7 + index * 0.1
          }} data-id="element-1115">
                <div className="flex items-center justify-between mb-2" data-id="element-1116">
                  <div className="flex items-center gap-2" data-id="element-1117">
                    <div className={`w-8 h-8 rounded-lg ${category.color} flex items-center justify-center`} data-id="element-1118">
                      <Icon className="w-4 h-4 text-white" strokeWidth={2} data-id="element-1119" />
                    </div>
                    <span className="text-sm font-medium" style={{
                  color: OPENFINANCE_THEME.colors.text
                }} data-id="element-1120">
                      {category.name}
                    </span>
                  </div>
                  <span className="text-sm font-bold" style={{
                color: OPENFINANCE_THEME.colors.text
              }} data-id="element-1121">
                    AED {category.amount.toLocaleString()}
                  </span>
                </div>
                <div className="w-full rounded-full h-2 overflow-hidden" style={{
              backgroundColor: OPENFINANCE_THEME.colors.borderLight
            }} data-id="element-1122">
                  <motion.div initial={{
                width: 0
              }} animate={{
                width: `${percentage}%`
              }} transition={{
                delay: 0.8 + index * 0.1,
                duration: 0.5
              }} className={`h-full ${category.color} rounded-full`} data-id="element-1123" />
                </div>
              </motion.div>;
        })}
        </div>
      </motion.div>

      {/* Recent Transactions */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.8
    }} className="bg-white rounded-2xl p-5 border-2 shadow-sm" style={{
      borderColor: OPENFINANCE_THEME.colors.border
    }} data-id="element-1124">
        <div className="flex items-center justify-between mb-4" data-id="element-1125">
          <div className="text-sm font-bold uppercase tracking-wide" style={{
          color: OPENFINANCE_THEME.colors.text
        }} data-id="element-1126">
            Recent Transactions
          </div>
          <button className="text-xs font-medium transition-colors" style={{
          color: OPENFINANCE_THEME.colors.primary500
        }} data-id="element-1127">
            View All
          </button>
        </div>

        <div className="space-y-2" data-id="element-1128">
          {recentTransactions.map((tx, i) => {
          const Icon = tx.icon;
          const isIncome = tx.amount > 0;
          return <motion.div key={i} initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.9 + i * 0.05
          }} className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors" data-id="element-1129">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0`} style={{
              backgroundColor: isIncome ? OPENFINANCE_THEME.colors.success + '15' : OPENFINANCE_THEME.colors.borderLight
            }} data-id="element-1130">
                  <Icon className={`w-5 h-5`} style={{
                color: isIncome ? OPENFINANCE_THEME.colors.success : OPENFINANCE_THEME.colors.textMuted
              }} strokeWidth={2} data-id="element-1131" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-1132">
                  <div className="text-sm font-semibold truncate" style={{
                color: OPENFINANCE_THEME.colors.text
              }} data-id="element-1133">
                    {tx.merchant}
                  </div>
                  <div className="text-xs" style={{
                color: OPENFINANCE_THEME.colors.textMuted
              }} data-id="element-1134">
                    {tx.category} • {tx.date}
                  </div>
                </div>
                <div className={`text-sm font-bold`} style={{
              color: isIncome ? OPENFINANCE_THEME.colors.success : OPENFINANCE_THEME.colors.text
            }} data-id="element-1135">
                  {isIncome ? '+' : ''}
                  {tx.amount.toFixed(2)} AED
                </div>
              </motion.div>;
        })}
        </div>
      </motion.div>

      {/* Smart Insight Card */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 1.0
    }} className="bg-gradient-to-br rounded-2xl p-5 text-white shadow-lg" style={{
      background: `linear-gradient(135deg, ${OPENFINANCE_THEME.colors.accent500} 0%, ${OPENFINANCE_THEME.colors.accent600} 100%)`
    }} data-id="element-1136">
        <div className="flex items-start gap-3" data-id="element-1137">
          <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0" data-id="element-1138">
            <TrendingUpIcon className="w-5 h-5" data-id="element-1139" />
          </div>
          <div className="flex-1" data-id="element-1140">
            <div className="text-sm font-bold mb-1" data-id="element-1141">💡 Smart Insight</div>
            <div className="text-sm opacity-90 leading-relaxed mb-3" data-id="element-1142">
              You're spending 15% less on dining out this month. Keep it up to
              reach your AED 5,000 savings goal 2 months faster!
            </div>
            <button className="text-xs font-semibold px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg transition-colors backdrop-blur-sm" data-id="element-1143">
              View Savings Plan
            </button>
          </div>
        </div>
      </motion.div>

      {/* You're in Control Section */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 1.1
    }} className="bg-white border-2 rounded-2xl p-6" style={{
      borderColor: OPENFINANCE_THEME.colors.success + '30'
    }} data-id="element-1144">
        <div className="flex items-start gap-3 mb-4" data-id="element-1145">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{
          backgroundColor: OPENFINANCE_THEME.colors.success + '15'
        }} data-id="element-1146">
            <ShieldCheckIcon className="w-5 h-5" style={{
            color: OPENFINANCE_THEME.colors.success
          }} data-id="element-1147" />
          </div>
          <div data-id="element-1148">
            <h3 className="text-lg font-bold mb-1" style={{
            color: OPENFINANCE_THEME.colors.text,
            fontFamily: OPENFINANCE_THEME.fonts.heading
          }} data-id="element-1149">
              You're Always in Control
            </h3>
            <p className="text-sm" style={{
            color: OPENFINANCE_THEME.colors.textMuted
          }} data-id="element-1150">
              Manage your Open Finance permissions anytime
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4 mb-4" data-id="element-1151">
          {[{
          icon: EyeIcon,
          label: 'View Access History',
          description: 'See when VeloX accessed your data'
        }, {
          icon: ClockIcon,
          label: 'Pause Anytime',
          description: 'Temporarily stop data sharing'
        }, {
          icon: XCircleIcon,
          label: 'Revoke Access',
          description: 'Remove permissions instantly'
        }].map((action, index) => {
          const Icon = action.icon;
          return <div key={index} className="text-center p-4 rounded-xl border-2" style={{
            borderColor: OPENFINANCE_THEME.colors.borderLight
          }} data-id="element-1152">
                <div className="w-10 h-10 mx-auto mb-2 rounded-lg flex items-center justify-center" style={{
              backgroundColor: OPENFINANCE_THEME.colors.primary500 + '15'
            }} data-id="element-1153">
                  <Icon className="w-5 h-5" style={{
                color: OPENFINANCE_THEME.colors.primary500
              }} data-id="element-1154" />
                </div>
                <div className="text-xs font-bold mb-1" style={{
              color: OPENFINANCE_THEME.colors.text
            }} data-id="element-1155">
                  {action.label}
                </div>
                <div className="text-xs" style={{
              color: OPENFINANCE_THEME.colors.textMuted
            }} data-id="element-1156">
                  {action.description}
                </div>
              </div>;
        })}
        </div>

        {onManageConsents && <button onClick={onManageConsents} className="w-full px-4 py-3 font-semibold rounded-xl flex items-center justify-center gap-2 transition-all hover:shadow-lg" style={{
        background: `linear-gradient(135deg, ${OPENFINANCE_THEME.colors.primary500} 0%, ${OPENFINANCE_THEME.colors.primary600} 100%)`,
        color: 'white'
      }} data-id="element-1157">
            <SettingsIcon className="w-5 h-5" data-id="element-1158" />
            <span data-id="element-1159">Manage Your Consents</span>
            <ArrowRightIcon className="w-4 h-4" data-id="element-1160" />
          </button>}
      </motion.div>
    </div>;
}